import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import LineOrderSwitch, { bySku } from '../../../shared/LineOrderSwitch';
import SaveButton from '../../../shared/SaveButton';
import RecordLookupField from '../../../shared/RecordLookupField';
import ActionsMenu from '../../../shared/ActionsMenu';
import useLineReorder from '../../../shared/useLineReorder';
import Accordion from '../../../shared/Accordion';
import ConvertedBanner from '../../../shared/ConvertedBanner';
import ReportOverlay from '../../../shared/ReportOverlay';
import PickSlipReportView from '../../warehouse/PickSlipReportView';
import SalesOrderReportView from './SalesOrderReportView';
import SalesOrderTimeline from './SalesOrderTimeline';
import SalesOrderStepper, { SalesOrderNextButton } from './SalesOrderStepper';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import '../../../shared/DetailTimelineLayout.css';
import { productLabel, customerLabel, formatPrice, formatUnitPrice } from '../Prices/priceLookupShared';
import { AvailabilityPill, CategoryPill, NeedsWeightPill, NotLinkedToQuickBooksPill } from '../../../shared/Pill';
import PriceCalculator from '../../../shared/PriceCalculator';
import LineUnitPriceField from '../../../shared/LineUnitPriceField';
import OverrideRevertBadge from '../../../shared/OverrideRevertBadge';
import OrderDiscountRow, { useOrderDiscount } from '../shared/OrderDiscount';
import { basisOf, priceLine, shownUnitPrice, shownLineAmount, documentTotals, netOfCancelled } from '../../../shared/pricingBasis';
import GstAwareCostField from '../../../shared/GstAwareCostField';
import ShippedNotice from '../../../shared/ShippedNotice';
import ShippingOptionsPicker, { computeDefaultCost } from '../../../shared/ShippingOptionsPicker';
import { carrierChoices, servicesForCarrier, carrierForService } from '../../../shared/shippingCarriage';
import AddressPicker, { fetchDeliveryAddresses } from '../../../shared/AddressPicker';
import ConfirmDialog from '../../../shared/ConfirmDialog';
import ShipConfirmDialog from '../../warehouse/ShipConfirmDialog';
import { isAustraliaPost } from '../../warehouse/packageShared';
import QuoteShippingEstimate, { estimateInputsKey, chargeTypedOver, shippingNotAdded, ShippingOverrideReason } from '../Quotes/QuoteShippingEstimate';
import useReestimateOnSave from '../shared/useReestimateOnSave';
import {
  Building2,
  Info,
  UserRound,
} from 'lucide-react';
import { todayInTenant, formatDate } from '../../../shared/tenantClock';
import DateInput from '../../../shared/DateInput';
import NumberInput from '../../../shared/NumberInput';
import { formatNumber, formatMoney } from '../../../shared/numberFormat';
import { useShipmentWeightUnit } from '../../../shared/useShipmentWeightUnit';
import { useScreenAccess, usePermissions } from '../../../shared/PermissionsContext';
import DocumentComments from '../shared/DocumentComments';
import DocumentPayments, { PaymentStatusPill, fetchPayments } from '../shared/DocumentPayments';
import useSaveKey from '../../../shared/useSaveKey';
import TaxBasisLine from '../../../shared/TaxBasisLine';
import { SegmentedToggle } from '../../../shared/DashboardChart';
import { GST_VIEWS, viewUnitPrice, viewLineAmount, viewSubtotal, viewDiscount } from '../../../shared/gstView';
import { askConfirm, askText } from '../../../shared/AppDialogs';

// Build/edit a Sales Order as one buffered document — mirrors
// QuotePage.js's shape closely: Customer + every line held in local state,
// nothing reaches the server until Save (POST for a new Sales Order, PUT
// for an existing Draft — see salesOrderController.js). Each line's price
// is previewed live via the same /api/price-lookup Price Lookup itself
// uses, then re-resolved fresh by the server at Save time. The Actions
// menu moves it Draft → Submitted (the Customer's commitment, made on their
// behalf) → Accepted (the Supplier's), then into picking. Print Pick Slip (PickSlipReportView.js) and Print Sales Order (SalesOrderReportView.js) reuse the same GET this
// page already loads — no separate reporting mechanism.
let rowKeySeq = 0;
function nextRowKey() {
  rowKeySeq += 1;
  return rowKeySeq;
}

// "6/10" while in progress, "Full" once it clears — split into their own
// Pick/Pack columns (each header already names what it is), so the
// cell itself carries no word, unlike PickOrderPage.js's own scan-feedback
// messages which spell out "of" for a standalone banner with no header
// alongside it.
function pickPackCount(doneQty, orderedQty) {
  const done = Number(doneQty || 0);
  const ordered = Number(orderedQty || 0);
  if (ordered > 0 && done >= ordered) return 'Full';
  return `${formatNumber(done, { maxDecimals: 4 })}/${formatNumber(ordered, { maxDecimals: 4 })}`;
}

// Freight on an order: its Shipping cost plus every Option's cost, ex GST,
// or null when there's neither ("to be added"). Same sum as
// salesOrderController.js's freightTotal, so the screen warns exactly when
// Save will add the freight line to the Comment to Customer.
function freightTotal(shippingCost, options) {
  const list = options || [];
  if ((shippingCost === null || shippingCost === undefined) && !list.length) return null;
  const optionsCost = list.reduce((sum, o) => sum + (Number(o.cost) || 0), 0);
  return Math.round(((Number(shippingCost) || 0) + optionsCost) * 100) / 100;
}

// The order's fulfilled date — the latest of its lines' fulfilled dates
// (each follows its shipments' Date Shipped, see backend
// services/shipmentDates.js). Null until a line has shipped.
function latestFulfilledDate(lines) {
  return (lines || []).reduce((latest, l) => (l.fulfilled_date && (!latest || l.fulfilled_date > latest) ? l.fulfilled_date : latest), null);
}

// The order's Completed date (issue #292): the latest of its lines'
// Completed dates once every line has one (shipped in full or closed
// short), otherwise null.
function orderCompletedDate(lines) {
  const open = (lines || []).filter((l) => Number(l.qty) > 0);
  if (!open.length || open.some((l) => !l.completed_date)) return null;
  return open.reduce((latest, l) => (!latest || l.completed_date > latest ? l.completed_date : latest), null);
}

function blankDraftRow() {
  return {
    productSearch: '', productId: '', product: null,
    results: [], showResults: false,
    uomOptions: [], uomId: '', qty: '1',
  };
}

function SalesOrderPage() {
  const { canCreate, canUpdate } = useScreenAccess();
  const { accessFor } = usePermissions();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();

  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // 'ex' / 'inc' once the Ex GST / Inc GST switch is used; null follows
  // the Customer's Pricing Basis.
  const [gstView, setGstView] = useState(null);
  const [orderMeta, setOrderMeta] = useState(null); // { display_number, status, createdAt }
  // Which print is open: 'pick-slip' or 'sales-order', null for neither.
  const [showReport, setShowReport] = useState(null);
  // Order Date on a brand-new (unsaved) order, defaulting to today (local
  // date, not UTC); a saved order carries its own in orderMeta.orderDate.
  // Line prices are looked up as of this date.
  const [newOrderDate, setNewOrderDate] = useState(() => todayInTenant());
  const orderDate = orderMeta ? orderMeta.orderDate : newOrderDate;

  // Captured once on mount, then scrubbed from history state — otherwise
  // the banner would resurface on a browser Back/Forward into this same
  // page after Save/Cancel navigated away, since router state persists in
  // history unless explicitly cleared.
  const [justConverted] = useState(() => !!location.state?.justConverted);
  // What kept a converted order from being Accepted on the way in, if
  // anything ("a delivery address").
  const [acceptMissing] = useState(() => location.state?.acceptMissing || null);
  // Or what wasn't linked to QuickBooks ("Acme Pty Ltd and Product ABC-1").
  const [acceptUnlinked] = useState(() => location.state?.acceptUnlinked || null);
  useEffect(() => {
    if (location.state?.justConverted) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // What isn't linked to QuickBooks on an order not yet Accepted ({
  // customer, products }, issue #268), or null — marked before Accept.
  const [quickbooksUnlinked, setQuickbooksUnlinked] = useState(null);

  // A line's reason its Product blocks Accept, or null (as saved).
  // Not linked to QuickBooks (issue #268) blocks Accept, so each unlinked
  // Customer/Product is marked where it sits on the order, opening its
  // QuickBooks tab on that record, ready to link.
  const QuickBooksLinkPill = ({ noun, id: recordId, reason }) => {
    const href = `/accounting-quickbooks?tab=${noun.toLowerCase()}s&${noun.toLowerCase()}=${recordId}`;
    return (
      <a
        href={href}
        onClick={async (e) => {
          e.stopPropagation();
          if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          if (hasUnsavedChanges() && !(await askConfirm('Leave without saving? Your changes to this Sales Order will be lost.', { confirmLabel: 'Leave', destructive: true }))) return;
          navigate(href);
        }}
        style={{ textDecoration: 'none' }}
      >
        <NotLinkedToQuickBooksPill noun={noun} reason={reason} linkable />
      </a>
    );
  };
  const unlinkedReasonFor = (line) => quickbooksUnlinked?.products.find((p) => String(p.id) === String(line.productId))?.reason || null;

  const [customerId, setCustomerId] = useState('');
  // The picked Customer as the Customer field shows it ({ id, label, type }).
  const [customer, setCustomer] = useState(null);

  const [addressId, setAddressId] = useState('');
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [poReference, setPoReference] = useState('');
  const [customerComment, setCustomerComment] = useState('');
  const [staffComment, setStaffComment] = useState('');
  // Carrier picked ahead of the Service (issue #281) — see QuotePage.js.
  const [carrierId, setCarrierId] = useState('');
  const [shippingServiceId, setShippingServiceId] = useState('');
  const [shippingServices, setShippingServices] = useState([]);
  // Tenant's configured unit for Product/UoM Weight (see
  // ProductWeightsPage.js) — g or kg, used only to label the Total Weight
  // figure below; the raw values summed are already in that same unit.
  const [weightUnit, setWeightUnit] = useState('');
  const shipmentWeightUnit = useShipmentWeightUnit();
  // Manually entered (issue #196) — always held here as ex-GST, null when
  // not yet entered. Locked (read-only) below whenever this Sales Order
  // was converted from a Quote (orderMeta.sourceQuote set) — the Customer
  // already approved the figure there; freely editable on a directly-
  // created Sales Order. See GstAwareCostField.js / QuotePage.js's own
  // shippingCost for the same shape.
  const [shippingCost, setShippingCost] = useState(null);
  const [shippingOverrideReason, setShippingOverrideReason] = useState('');
  const discount = useOrderDiscount();
  // Selected Shipping Options (issue #193) — same shape/lock rule as
  // shippingCost above. See QuotePage.js's own selectedOptions.
  const [selectedOptions, setSelectedOptions] = useState([]);
  // Australia Post shipping estimate (issue #351), the same as a Quote's:
  // the Packaging it's sized by, and the last Estimate Shipping result
  // (saved with the order).
  const [estimatePackagingId, setEstimatePackagingId] = useState('');
  const [shippingEstimate, setShippingEstimate] = useState(null);
  const [packagings, setPackagings] = useState([]);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState('');
  const [customerTax, setCustomerTax] = useState(null);
  // Tracking Numbers (issue #206) — read-only here now (adding/editing/
  // deleting moved to ShipmentsTable.js, its own cross-order screen); this
  // page only still reads them for its own Total Shipment Weight figure
  // (see totalShipmentWeight below).
  const [trackingNumbers, setTrackingNumbers] = useState([]);

  const [lines, setLines] = useState([]); // committed rows: { key, productId, product, uomOptions, uomId, qty, unitPrice, extendedPrice, referencePrice }
  const [draftRow, setDraftRow] = useState(blankDraftRow());
  const draftSearchTimer = useRef(null);

  // Shared by the initial load and the "Refresh Sales Order" action — GET
  // already re-resolves a Draft's prices *and* GST server-side (same
  // resolveSalesOrderLines call — see salesOrderController.js), so
  // re-running this same fetch is all a manual refresh needs to do.
  const loadOrder = async () => {
    const res = await axios.get(`/api/sales-orders/${id}`);
    const order = res.data.data;
    setQuickbooksUnlinked(res.data.quickbooks_unlinked || null);
    setOrderMeta({ display_number: order.display_number, status: order.status, createdAt: order.createdAt, orderDate: order.order_date, sourceQuote: order.source_quote, onHold: order.on_hold, onHoldReason: order.on_hold_reason, editRules: order.edit_rules, chargeInvoiced: !!order.shipping_charge_invoiced, portalLogin: order.portal_login_id ? (order.portal_login?.email || 'a login since removed') : null, customerNote: order.customer_note || '', fulfilledDate: latestFulfilledDate(order.lines), completedDate: orderCompletedDate(order.lines), savedCarriage: { carrier_id: order.carrier_id, shipping_service: order.shipping_service, selected_options: order.selected_options }, savedFreight: freightTotal(order.shipping_cost, order.selected_options), savedShippingBlank: order.shipping_cost === null || order.shipping_cost === undefined, savedOptionsFreight: freightTotal(null, order.selected_options), originLabel: order.origin_label, createdByName: order.created_by_name });
    setCustomerId(String(order.customer.id));
    setCustomer({ id: order.customer.id, label: customerLabel(order.customer), type: order.customer.type });
    setAddressId(order.address ? String(order.address.id) : '');
    setCustomerAddresses(await fetchDeliveryAddresses(order.customer.id, order.address?.id));
    setPoReference(order.po_reference || '');
    setCustomerComment(order.customer_comment || '');
    setStaffComment(order.staff_comment || '');
    setCarrierId(order.carrier_id != null ? String(order.carrier_id) : '');
    setShippingServiceId(order.shipping_service ? String(order.shipping_service.id) : '');
    setShippingCost(order.shipping_cost !== null && order.shipping_cost !== undefined ? Number(order.shipping_cost) : null);
    setShippingOverrideReason(order.shipping_override_reason || '');
    discount.load(order);
    setSelectedOptions((order.selected_options || []).map((o) => ({
      shipping_option_id: o.shipping_option.id,
      cost: Number(o.cost),
      overridden: false,
    })));
    setEstimatePackagingId(order.estimate_packaging_id != null ? String(order.estimate_packaging_id) : '');
    setShippingEstimate(order.shipping_estimate || null);
    setEstimateError('');
    setCustomerTax({
      tax_handling_type: order.customer.tax_handling_type,
      payment_term: order.customer.payment_term,
    });
    setTrackingNumbers(order.shipments || []);

    const loadedLines = await Promise.all((order.lines || []).map(async (line) => {
      let uomOptions = [];
      let allUomOptions = [];
      try {
        const uomRes = await axios.get(`/api/products/${line.product.id}/uoms`);
        allUomOptions = uomRes.data.data?.uoms || [];
        uomOptions = allUomOptions.filter((pu) => pu.can_sell);
      } catch (err) {
        uomOptions = [];
        allUomOptions = [];
      }
      return {
        key: nextRowKey(),
        id: line.id,
        productId: String(line.product.id),
        product: line.product,
        uomOptions,
        allUomOptions,
        uomId: String(line.uom.id),
        qty: String(line.qty),
        unitPrice: line.unit_price,
        unitPriceInc: line.unit_price_inc,
        extendedPrice: line.extended_price,
        referencePrice: line.reference_price,
        gstAmount: line.gst_amount,
        gstRate: line.gst_rate,
        extendedPriceIncGst: line.extended_price_inc_gst,
        overridden: line.overridden,
        overrideReason: line.override_reason || '',
        // The saved price this line holds while the order's prices are held
        // (pricesHeld below, issue #280) — a qty change keeps it, and
        // reverting an override restores it. Null once a fresh lookup
        // replaces it (UoM change), or for a line that was overridden.
        heldUnitPrice: line.overridden ? null : line.unit_price,
        heldUnitPriceInc: line.overridden ? null : line.unit_price_inc,
        // Read-only here — recorded via the Pick/Pack/Put scan flow
        // (PickOrderPage.js) or the Ship action below, never edited
        // directly on this desktop form.
        qtyPicked: line.qty_picked,
        qtyPacked: line.qty_packed,
        qtyShipped: line.qty_shipped,
        qtyCancelled: line.qty_cancelled,
        backordered: line.backordered,
        fulfilledDate: line.fulfilled_date,
        completedDate: line.completed_date,
      };
    }));
    setLines(loadedLines);
    setSnapshotDue(true);
    return order;
  };

  useEffect(() => {
    axios.get('/api/shipping-services', { params: { status: 'all' } })
      .then((res) => setShippingServices(res.data.data || []))
      .catch(() => setShippingServices([]));
  }, []);

  useEffect(() => {
    axios.get('/api/platform-settings')
      .then((res) => setWeightUnit(res.data.data?.weight_unit || ''))
      .catch(() => setWeightUnit(''));
  }, []);

  useEffect(() => {
    axios.get('/api/packagings', { params: { status: 'all' } })
      .then((res) => setPackagings(res.data.data || []))
      .catch(() => setPackagings([]));
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    loadOrder()
      .then((order) => {
        if (order.refresh_error) {
          showError(undefined, `Pricing/GST could not be refreshed: ${order.refresh_error}`);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [refreshingOrder, setRefreshingOrder] = useState(false);
  const handleRefreshOrder = async () => {
    setRefreshingOrder(true);
    try {
      const order = await loadOrder();
      if (order.refresh_error) {
        showError(undefined, `Pricing/GST could not be refreshed: ${order.refresh_error}`);
      } else {
        dismissError();
      }
    } catch (err) {
      showError(err, 'Failed to refresh Sales Order');
    }
    setRefreshingOrder(false);
  };

  // On Hold is a pause layered on top of whatever status the order is
  // already in, available at any stage (waiting on stock, shipping,
  // insurance, an open question — not just stock shortages). Repeatable:
  // holding/releasing an already-held order just updates the reason and
  // records a fresh milestone (see salesOrderController.holdSalesOrder).
  // A typed-value dialog (AppDialogs.js), not a form drawer, since this is
  // a single optional line of free text. Cancel leaves the hold as it was.
  const handleHold = async () => {
    const reason = await askText('Reason for putting this Sales Order on hold (optional):', { title: 'Put on Hold', confirmLabel: 'Put on Hold' });
    if (reason === null) return;
    try {
      await axios.patch(`/api/sales-orders/${id}/hold`, { reason: reason.trim() || undefined });
      setOrderMeta((prev) => ({ ...prev, onHold: true, onHoldReason: reason.trim() || null }));
      dismissError();
    } catch (err) {
      showError(err, 'Failed to put Sales Order on hold');
    }
  };

  const handleReleaseHold = async () => {
    const reason = await askText('Reason for releasing this hold (optional):', { title: 'Release Hold', confirmLabel: 'Release Hold' });
    if (reason === null) return;
    try {
      await axios.patch(`/api/sales-orders/${id}/release-hold`, { reason: reason.trim() || undefined });
      setOrderMeta((prev) => ({ ...prev, onHold: false, onHoldReason: null }));
      dismissError();
    } catch (err) {
      showError(err, 'Failed to release hold');
    }
  };

  // Ship (issue #205) — order-level, replacing the old per-line "Scan to
  // Ship". Ships whatever's currently Packed but not yet Shipped, for every
  // line, in one pass; the confirmation dialog previews that outcome
  // (full/partial+backorder/none per line, see ShipConfirmSummary.js)
  // before anything commits. Repeatable — a later pass picks up whatever's
  // newly Packed-but-not-yet-Shipped since the previous one.
  // Ship takes one package (issue #299), so the dialog opens on the order
  // as the warehouse sees it — its lines and unshipped packages.
  const [shipOrder, setShipOrder] = useState(null);
  const shipDialogOpen = !!shipOrder;
  const setShipDialogOpen = (open) => { if (!open) setShipOrder(null); };
  const [shipping, setShipping] = useState(false);
  // The shipment the last Ship made, with its Invoice (issue #269), shown
  // until closed.
  const [shippedShipment, setShippedShipment] = useState(null);
  const canShip = lines.some((l) => Math.min(Number(l.qty) || 0, Number(l.qtyPacked || 0)) - Number(l.qtyShipped || 0) > 0);
  const openShipDialog = async () => {
    try {
      const res = await axios.get(`/api/fulfilment/sales_order/${id}`);
      // An unpaid Prepay order says so before the Ship form opens.
      if (res.data.data.ship_blocked) {
        showError({ response: { data: res.data.data.ship_blocked } });
        return;
      }
      setShipOrder(res.data.data);
    } catch (err) {
      showError(err, 'Failed to load the order’s packages');
    }
  };

  const handleConfirmShip = async (payload) => {
    setShipping(true);
    try {
      const res = await axios.post(`/api/sales-orders/${id}/ship`, payload);
      await loadOrder();
      setShipDialogOpen(false);
      setShippedShipment(res.data.tracking || null);
      dismissError();
    } catch (err) {
      showError(err, 'Failed to ship Sales Order');
    }
    setShipping(false);
  };

  // Cancel Backorder — closes out some or all of a backordered line's
  // outstanding qty (customer no longer wants to wait for it). A reason is
  // required (see cancelLineBackorder) since it's the whole point of
  // recording this as its own history entry rather than just editing Qty
  // down on the regular form.
  const [cancelDialogLine, setCancelDialogLine] = useState(null);
  const [cancelQty, setCancelQty] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const outstandingQty = (line) => Math.max(0, Number(line.qty) - Number(line.qtyShipped || 0) - Number(line.qtyCancelled || 0));

  const handleOpenCancelDialog = (line) => {
    setCancelDialogLine(line);
    setCancelQty(String(outstandingQty(line)));
    setCancelReason('');
  };

  const cancelQtyValid = Number(cancelQty) > 0 && cancelDialogLine && Number(cancelQty) <= outstandingQty(cancelDialogLine);
  const canConfirmCancel = cancelQtyValid && cancelReason.trim().length > 0;

  const handleConfirmCancelBackorder = async () => {
    if (!canConfirmCancel) return;
    setCancelling(true);
    try {
      await axios.patch(`/api/sales-orders/${id}/lines/${cancelDialogLine.id}/cancel-backorder`, {
        qty: Number(cancelQty),
        reason: cancelReason.trim(),
      });
      await loadOrder();
      setCancelDialogLine(null);
      dismissError();
    } catch (err) {
      showError(err, 'Failed to cancel backorder');
    }
    setCancelling(false);
  };

  // --- Customer picker ---

  const searchCustomers = (term) => axios
    .get('/api/customers', { params: { scope: 'quotable', search: term, status: 'active', limit: 10 } })
    .then((res) => res.data.data || []);

  // Same as QuotePage.js's loadAddressesFor.
  const addressRequest = useRef(0);
  const loadAddressesFor = async (customerIdToLoad) => {
    const request = ++addressRequest.current;
    const addresses = await fetchDeliveryAddresses(customerIdToLoad).catch(() => []);
    if (request !== addressRequest.current) return;
    setCustomerAddresses(addresses);
    const own = addresses.filter((a) => !a.recipient);
    if (own.length === 1) setAddressId(String(own[0].id));
  };

  const applyCustomer = (c) => {
    setCustomerId(String(c.id));
    setCustomer({ id: c.id, label: customerLabel(c), type: c.type });
    setGstView(null);
    setCustomerAddresses([]);
    setAddressId('');
    loadAddressesFor(c.id);
    setCarrierId(c.carrier_id != null ? String(c.carrier_id) : '');
    setShippingServiceId(c.shipping_service_id != null ? String(c.shipping_service_id) : '');
    // Cascades the Customer's own default Option selections onto this new
    // Sales Order — same reasoning as QuotePage.js's own applyCustomer.
    setSelectedOptions((c.default_options || []).map((o) => ({
      shipping_option_id: o.shipping_option.id,
      cost: computeDefaultCost(o.shipping_option, 0),
      overridden: false,
    })));
    setCustomerTax({
      tax_handling_type: c.tax_handling_type,
      payment_term: c.payment_term,
    });
  };

  // Preload from a linking screen's own kebab action (same shape
  // QuotePage.js's preload already supports), which hands the
  // already-loaded CommercialEntity record through router state.
  useEffect(() => {
    if (id) return;
    const preloadCustomer = location.state?.customer;
    if (preloadCustomer) applyCustomer(preloadCustomer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectCustomer = async (c) => {
    if (lines.length > 0 && String(c.id) !== customerId) {
      if (!(await askConfirm('Changing the Customer clears all lines, since prices are Customer-specific. Continue?', { confirmLabel: 'Change Customer', destructive: true }))) return;
      setLines([]);
    }
    applyCustomer(c);
  };

  // --- Line pricing ---

  // A Draft's prices are provisional and follow its Order Date; once
  // Submitted they're firm — what the Customer committed to — and an order
  // converted from an Accepted Quote holds the Quote's prices from the
  // start. Held prices never re-price on a date or qty change — only a
  // manual override, or a newly added line, priced as of today once
  // Submitted. Mirrors salesOrderController.js's pricesHeld.
  const submitted = !!orderMeta && orderMeta.status !== 'draft';
  const pricesHeld = submitted || !!orderMeta?.sourceQuote;
  const linePriceDate = submitted ? todayInTenant() : orderDate;

  const fetchLinePreview = async (productId, uomId, qty, asOfDate = linePriceDate) => {
    if (!customerId || !productId || !uomId || !qty || Number(qty) < 1) return null;
    try {
      const res = await axios.get('/api/price-lookup', {
        params: { product_id: productId, customer_id: customerId, uom_id: uomId, qty, as_of_date: asOfDate || undefined },
      });
      const row = res.data.data?.rows?.[0];
      return row ? {
        unitPrice: row.unitPrice,
        unitPriceInc: row.unitPriceInc,
        referencePrice: row.referencePrice,
        gstRate: row.gstRate,
      } : null;
    } catch (err) {
      return null;
    }
  };

  const refreshLinePreview = async (key, productId, uomId, qty) => {
    const preview = await fetchLinePreview(productId, uomId, qty);
    setLines((prev) => prev.map((l) => (l.key === key
      ? {
        ...previewedLine({ ...l, qty }, preview),
        // A fresh system lookup always supersedes a prior override —
        // covers both qty/UoM change and the explicit revert action below.
        overridden: false,
        overrideReason: '',
        heldUnitPrice: null,
        heldUnitPriceInc: null,
      }
      : l)));
  };

  // Recalculates a line's figures from a given price ({ ex } or { inc }) at
  // its current qty, in the Customer's Pricing Basis — the same formula
  // resolveSalesOrderLines uses server-side (shared/pricingBasis.js). A line
  // priced at an inc figure on a Retail Customer keeps that exact figure.
  const customerGstRate = () => Number(customerTax?.tax_handling_type?.tax_code ? customerTax.tax_handling_type.tax_code.rate : 0) || 0;
  const repriceLineLocally = (l, { ex = null, inc = null }, extra = {}, lineBasis = basisOf(customerTax?.tax_handling_type)) => {
    const rate = Number(l.gstRate ?? customerGstRate()) || 0;
    const figures = priceLine({ unitPriceEx: ex, unitPriceInc: inc, qty: l.qty, gstRate: rate, basis: lineBasis });
    return { ...l, ...figures, gstRate: rate, ...extra };
  };

  // A fresh Price Lookup result applied to a line.
  const previewedLine = (l, preview) => {
    if (!preview) {
      return { ...l, unitPrice: null, unitPriceInc: null, extendedPrice: null, referencePrice: null, gstAmount: null, gstRate: null, extendedPriceIncGst: null };
    }
    return repriceLineLocally(
      { ...l, gstRate: preview.gstRate, referencePrice: preview.referencePrice },
      { ex: preview.unitPrice, inc: preview.unitPriceInc },
    );
  };

  // A line's current price, as repriceLineLocally takes it.
  const currentPrice = (l) => ({ ex: l.unitPrice, inc: l.unitPriceInc });
  // A held line keeps the basis it was saved under — one saved with an inc
  // price stays sold at it (mirrors salesOrderLineResolution.js).
  const heldBasis = (l) => ({ retail: l.heldUnitPriceInc !== null && l.heldUnitPriceInc !== undefined, roundingCents: 1 });

  // A line holding its saved price (see heldUnitPrice) keeps it.
  const holdsPrice = (l) => pricesHeld && !l.overridden && l.heldUnitPrice !== null && l.heldUnitPrice !== undefined;

  // A salesperson-entered price for a genuine one-off concession (see
  // vis_sales_orders.md's Pricing a Sales Order Line) — extended/GST are
  // still derived from it locally the same way resolveSalesOrderLines
  // derives them server-side, so the on-screen totals stay in sync until
  // Save re-resolves for real.
  // Typed in the Customer's basis — inc for Retail, ex for Business.
  const handleOverridePrice = (key, value) => {
    const price = Number(value);
    if (!Number.isFinite(price) || price < 0) return;
    const typed = basisOf(customerTax?.tax_handling_type).retail ? { inc: price } : { ex: price };
    setLines((prev) => prev.map((l) => (l.key === key ? repriceLineLocally(l, typed, { overridden: true }) : l)));
  };

  const handleOverrideReasonChange = (key, value) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, overrideReason: value } : l)));
  };

  const handleRevertOverride = (key) => {
    const line = lines.find((l) => l.key === key);
    if (!line) return;
    // Back to the held price this line had before the override.
    if (pricesHeld && line.heldUnitPrice !== null && line.heldUnitPrice !== undefined) {
      setLines((prev) => prev.map((l) => (l.key === key
        ? repriceLineLocally(l, { ex: l.heldUnitPrice, inc: l.heldUnitPriceInc }, { overridden: false, overrideReason: '' }, heldBasis(l))
        : l)));
      return;
    }
    refreshLinePreview(key, line.productId, line.uomId, line.qty);
  };

  // --- Draft (bottom, always-blank) row ---

  const handleDraftSearchChange = (value) => {
    setDraftRow((prev) => ({ ...prev, productSearch: value, productId: '', product: null }));
    clearTimeout(draftSearchTimer.current);
    if (!value.trim()) {
      setDraftRow((prev) => ({ ...prev, results: [], showResults: false }));
      return;
    }
    draftSearchTimer.current = setTimeout(() => {
      axios.get('/api/products', { params: { search: value, status: 'active', limit: 10 } })
        .then((res) => setDraftRow((prev) => ({ ...prev, results: res.data.data || [], showResults: true })))
        .catch(() => setDraftRow((prev) => ({ ...prev, results: [] })));
    }, 250);
  };

  const handleDraftSelectProduct = async (product) => {
    let uomOptions = [];
    let allUomOptions = [];
    try {
      const res = await axios.get(`/api/products/${product.id}/uoms`);
      allUomOptions = res.data.data?.uoms || [];
      uomOptions = allUomOptions.filter((pu) => pu.can_sell);
    } catch (err) {
      uomOptions = [];
      allUomOptions = [];
    }
    const firstUom = uomOptions[0];
    const key = nextRowKey();
    const newLine = {
      key,
      productId: String(product.id),
      product,
      uomOptions,
      allUomOptions,
      uomId: firstUom ? String(firstUom.uom_id) : '',
      qty: firstUom ? String(firstUom.min_order_qty) : '1',
      unitPrice: null,
      unitPriceInc: null,
      extendedPrice: null,
      referencePrice: null,
      gstAmount: null,
      gstRate: null,
      extendedPriceIncGst: null,
      overridden: false,
      overrideReason: '',
    };
    setLines((prev) => [...prev, newLine]);
    setDraftRow(blankDraftRow());
    if (firstUom) refreshLinePreview(key, product.id, firstUom.uom_id, newLine.qty);
  };

  // --- Committed line edits ---

  const handleLineUomChange = (key, uomId) => {
    setLines((prev) => prev.map((l) => {
      if (l.key !== key) return l;
      const uom = l.uomOptions.find((pu) => String(pu.uom_id) === String(uomId));
      return { ...l, uomId, qty: uom ? String(uom.min_order_qty) : l.qty };
    }));
    const line = lines.find((l) => l.key === key);
    const uom = line?.uomOptions.find((pu) => String(pu.uom_id) === String(uomId));
    if (line) refreshLinePreview(key, line.productId, uomId, uom ? String(uom.min_order_qty) : line.qty);
  };

  const handleLineQtyChange = (key, value) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, qty: value } : l)));
  };

  const handleLineQtyBlur = (key) => {
    const line = lines.find((l) => l.key === key);
    if (!line) return;
    // A held price (or an override) stays put; only the Amount follows qty.
    if (holdsPrice(line) || (pricesHeld && line.overridden)) {
      setLines((prev) => prev.map((l) => (l.key === key
        ? repriceLineLocally(l, currentPrice(l), {}, holdsPrice(l) ? heldBasis(l) : undefined)
        : l)));
      return;
    }
    refreshLinePreview(key, line.productId, line.uomId, line.qty);
  };

  const handleRemoveLine = (key) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
  };

  // --- Save / Cancel ---

  const anyNeedsPrice = lines.some((l) => l.unitPrice === null || l.unitPrice === undefined);

  // Total Weight — sums qty × the line's sold Product/UoM Weight (see
  // ProductWeightsPage.js; weight lives per-UoM, not per-Product). Read
  // straight off allUomOptions, already fetched per line for the UoM
  // dropdown — no extra request needed. A line whose Product/UoM has no
  // Weight set is excluded from the sum (not treated as 0) and flags
  // anyMissingWeight so the total reads as a lower bound, not a false total.
  const lineWeight = (l) => {
    const uom = (l.allUomOptions || l.uomOptions || []).find((pu) => String(pu.uom_id) === String(l.uomId));
    return uom && uom.weight !== null && uom.weight !== undefined ? Number(uom.weight) : null;
  };
  const totalWeight = lines.reduce((sum, l) => {
    const w = lineWeight(l);
    return w === null ? sum : sum + w * (Number(l.qty) || 0);
  }, 0);
  const anyMissingWeight = lines.some((l) => lineWeight(l) === null);
  // Sum of every shipment's weight (see the Shipments list): its outermost
  // packages' added up, each its own weight or else the packages directly
  // inside it when every one was weighed (issues #299, #313). Shipments
  // with no weight are skipped; the row only renders when at least one has
  // one.
  const hasWeight = (p) => p.weight !== null && p.weight !== undefined && p.weight !== '';
  const shipmentWeightOf = (t) => {
    const pkgs = t.packages || [];
    const weightOf = (pkg) => {
      if (hasWeight(pkg)) return Number(pkg.weight);
      const inner = pkgs.filter((p) => String(p.parent_package_id) === String(pkg.id)).map(weightOf);
      return inner.length && inner.every((w) => w !== null) ? inner.reduce((sum, w) => sum + w, 0) : null;
    };
    const outers = pkgs.filter((p) => !p.parent_package_id || !pkgs.some((o) => String(o.id) === String(p.parent_package_id))).map(weightOf);
    return outers.length && outers.every((w) => w !== null) ? outers.reduce((sum, w) => sum + w, 0) : null;
  };
  const shipmentsWithWeight = trackingNumbers.filter((t) => !t.reversed_at && shipmentWeightOf(t) !== null);
  const totalShipmentWeight = shipmentsWithWeight.reduce((sum, t) => sum + shipmentWeightOf(t), 0);
  const formatWeight = (n) => `${formatNumber(Number(n || 0), { maxDecimals: 2 })} ${weightUnit || 'kg'}`;
  // Shipment weights are in the Shipment Weight Unit, not the Product
  // UoM Weight Unit the Total Weight estimate above uses.
  const formatShipmentWeight = (n) => `${formatNumber(Number(n || 0), { decimals: 1 })}${shipmentWeightUnit ? ` ${shipmentWeightUnit}` : ''}`;

  // Same cascade and display rules QuotePage.js uses: every price on the
  // form is in the Customer's Pricing Basis — inc GST for Retail, ex GST for
  // Business — one figure per row.
  const taxHandling = customerTax?.tax_handling_type;
  const gstApplicable = !!taxHandling?.tax_code && Number(taxHandling.tax_code.rate) > 0;
  const basis = basisOf(taxHandling);
  // The Ex GST / Inc GST switch (shared/gstView.js) starts on that basis
  // and can show the other side to reconcile against QuickBooks — view
  // only, so Unit Price turns read-only while it's off the Customer's basis.
  const viewInc = gstApplicable && (gstView ? gstView === 'inc' : basis.retail);
  const offBasis = gstApplicable && viewInc !== basis.retail;
  const basisSuffix = gstApplicable ? (viewInc ? ' (inc GST)' : ' (ex GST)') : '';
  // The totals follow the switch too: Subtotal, Shipping and Options Cost
  // all ex GST (then GST, then the Total) or all inc GST (adding up to the
  // Total, which includes the GST), so they add up down the column.
  const basisLine = <TaxBasisLine>{basisSuffix.slice(2, -1)}</TaxBasisLine>;
  const lineGstRate = (line) => line.gstRate ?? taxHandling?.tax_code?.rate;
  const linePrice = (line) => (offBasis ? viewUnitPrice(line, lineGstRate(line), viewInc) : shownUnitPrice(line, lineGstRate(line), basis));
  // A cancelled backorder's share counts in neither the Amount nor the totals.
  const amountLines = lines.map(netOfCancelled);
  const lineAmount = (line) => (offBasis ? viewLineAmount(netOfCancelled(line), viewInc) : shownLineAmount(netOfCancelled(line), basis));
  // A pack line's price per base unit (e.g. per PCS), shown under
  // Unit Price to match the base qty under UoM — what gets keyed into
  // QuickBooks, whose items are held in the base unit. Up to 4 decimals so
  // a pack price split across its units isn't rounded away.
  const baseUnitPriceLabel = (line) => {
    if (linePrice(line) === null) return null;
    const selectedUom = line.uomOptions.find((pu) => String(pu.uom_id) === String(line.uomId));
    const baseUom = (line.allUomOptions || line.uomOptions).find((pu) => pu.is_base);
    const rate = Number(selectedUom?.conversion_rate);
    if (!selectedUom || !baseUom || selectedUom.is_base || !(rate > 0)) return null;
    const price = linePrice(line) / rate;
    const decimals = Number(price.toFixed(2)) === Number(price.toFixed(4)) ? 2 : 4;
    return `${formatMoney(price, { decimals })} / ${baseUom.code || baseUom.name}`;
  };

  // Shipping Cost (issue #196) and Options Cost (issue #193) fold into the
  // same single GST figure. GST is rounded once for the whole document
  // (shared/pricingBasis.js).
  const gstRate = Number(taxHandling?.tax_code?.rate) || 0;
  const shippingCostEx = Number(shippingCost || 0);
  const optionsCostEx = selectedOptions.reduce((sum, o) => sum + (Number(o.cost) || 0), 0);
  const totals = documentTotals({ lines: amountLines, shippingEx: shippingCostEx, optionsEx: optionsCostEx, gstRate: gstApplicable ? gstRate : 0, basis, discount: discount.forTotals });
  const subtotal = amountLines.reduce((sum, l) => sum + (l.extendedPrice ? Number(l.extendedPrice) : 0), 0);
  // Editable at every Status up to and including Partially Shipped; only
  // Shipped locks the form (issue #205) — Customer/Address/PO/lines all
  // become read-only (Comments and Shipping excepted, shippedEditable
  // below). Tracking Numbers and Hold
  // stay exempt (see their own handlers above — deliberately no status
  // gate, same reasoning as before this story).
  // Also read-only without Create (new) or Update (existing) on Sales
  // Orders (issue #252).
  const readOnly = orderMeta?.status === 'shipped' || !(id ? canUpdate : canCreate);
  // What can change at this status (the lifecycle's Editable rules, shown
  // on Document Types). A Quote-sourced order past Draft keeps the Quote's
  // Customer, lines, qty and prices — Revise opens them again. Shipping
  // stays editable up to Shipped either way.
  const linesLocked = !!orderMeta?.editRules && !orderMeta.editRules.lines;
  const linesReadOnly = readOnly || linesLocked;
  // Lines are saved in the order they were added or dragged into — the
  // customer's order (issue #304). The Sequence switch only changes how
  // they're shown; dragging is off while they're shown in SKU order.
  const [lineOrder, setLineOrder] = useState('document');
  const shownLines = lineOrder === 'sku' ? bySku(lines) : lines;
  const { rowProps, handle } = useLineReorder(setLines, !linesReadOnly && lineOrder === 'document');
  // A Shipped order still takes its Comments and Shipping — a customer
  // query after shipping, or freight settled on another order or charged
  // later. Save comes back for those; everything else stays locked.
  const shippedEditable = orderMeta?.status === 'shipped' && canUpdate;
  const canSave = !readOnly || shippedEditable;
  // Its shipping charge is fixed while the order has an Invoice that isn't
  // void (issue #269): Shipping Cost, Options and Estimate Shipping lock.
  const chargeLocked = !!orderMeta?.chargeInvoiced;
  const costFieldLocked = !canSave || chargeLocked;

  // Estimate Shipping (issue #351) — only for an Australia Post Service,
  // the same as a Quote's (QuotePage.js).
  const selectedShippingService = shippingServices.find((sm) => String(sm.id) === String(shippingServiceId));
  const shippingCostBlank = shippingCost === null || shippingCost === undefined;
  const canEstimate = isAustraliaPost(selectedShippingService?.supplier?.name);
  const estimate = canEstimate ? shippingEstimate : null;
  const currentInputsKey = estimateInputsKey({ addressId, shippingServiceId, packagingId: estimatePackagingId, selectedOptions, lines });
  const typedOver = chargeTypedOver(estimate, shippingCost);

  // Order Date locks earlier than the whole-form readOnly above — as soon
  // as any line has shipped (partially_shipped or shipped), not only once
  // fully Shipped (issue #213).
  const isOrderDateEditable = !['partially_shipped', 'shipped'].includes(orderMeta?.status);
  const [savingOrderDate, setSavingOrderDate] = useState(false);

  // Line prices follow the Order Date, so a date change re-prices every
  // non-overridden line as of the new date (same lookup the line editor
  // uses). Overridden lines keep their salesperson-entered price.
  const repriceLinesForDate = async (newDate) => {
    const previews = await Promise.all(lines.map(async (l) => (
      l.overridden ? null : [l.key, await fetchLinePreview(l.productId, l.uomId, l.qty, newDate)]
    )));
    const byKey = new Map(previews.filter(Boolean));
    setLines((prev) => prev.map((l) => (byKey.has(l.key) ? previewedLine(l, byKey.get(l.key)) : l)));
  };

  const handleOrderDateChange = async (e) => {
    const newDate = e.target.value;
    if (!newDate) return;
    if (!orderMeta) {
      setNewOrderDate(newDate);
      await repriceLinesForDate(newDate);
      return;
    }
    const previous = orderMeta.orderDate;
    setOrderMeta((prev) => ({ ...prev, orderDate: newDate }));
    setSavingOrderDate(true);
    try {
      await axios.patch(`/api/sales-orders/${id}/date`, { order_date: newDate });
      dismissError();
      setHistoryVersion((v) => v + 1);
      if (!pricesHeld) await repriceLinesForDate(newDate);
    } catch (err) {
      setOrderMeta((prev) => ({ ...prev, orderDate: previous }));
      showError(err, 'Failed to update Order Date');
    }
    setSavingOrderDate(false);
  };

  const buildPayload = () => ({
    customer_id: customerId,
    address_id: addressId || null,
    po_reference: poReference || null,
    customer_comment: customerComment.trim() || null,
    staff_comment: staffComment.trim() || null,
    carrier_id: carrierId || null,
    shipping_service_id: shippingServiceId || null,
    shipping_cost: shippingCost,
    options: selectedOptions.map((o) => ({ shipping_option_id: o.shipping_option_id, cost: o.cost })),
    estimate_packaging_id: canEstimate ? (estimatePackagingId || null) : null,
    shipping_estimate: estimate,
    shipping_override_reason: typedOver ? (shippingOverrideReason.trim() || null) : null,
    ...discount.payload,
    lines: lines.map((l) => ({
      product_id: l.productId,
      uom_id: l.uomId,
      qty: Number(l.qty),
      overridden: !!l.overridden,
      // An override goes back in the basis it was typed in.
      unit_price: l.overridden && !basis.retail ? Number(l.unitPrice) : undefined,
      unit_price_inc: l.overridden && basis.retail ? Number(l.unitPriceInc) : undefined,
      override_reason: l.overridden ? (l.overrideReason || null) : undefined,
    })),
  });

  // Prices the order as it is on screen in the given Packaging — what
  // Estimate Shipping, and a re-estimate at Save, send.
  const requestEstimate = async (packagingId) => {
    const { lines: payloadLines, options, customer_id: payloadCustomer, address_id: payloadAddress, shipping_service_id: payloadService } = buildPayload();
    const res = await axios.post('/api/sales-orders/shipping-estimate', {
      customer_id: payloadCustomer,
      address_id: payloadAddress,
      shipping_service_id: payloadService,
      packaging_id: packagingId || null,
      options,
      lines: payloadLines,
      inputs_key: estimateInputsKey({ addressId, shippingServiceId, packagingId, selectedOptions, lines }),
      // Shipping Charge Rounding lands on the figure the Customer sees.
      gst_rate: gstApplicable ? gstRate : 0,
    });
    return res.data.data;
  };
  const showEstimate = (next, packagingId) => {
    setEstimatePackagingId(packagingId || '');
    setShippingEstimate(next);
    setShippingCost(Number(next.charge));
    setShippingOverrideReason('');
  };

  // Re-estimate Shipping on Save (issue #437), as QuotePage.js's; an
  // invoiced charge is fixed, so it never asks.
  const { confirmReestimate, reestimateDialog, settle: settleInputsKey } = useReestimateOnSave({
    estimate, currentInputsKey, shippingCost, typedOver, locked: chargeLocked,
    packagings, packagingId: estimatePackagingId, requestEstimate, onReestimated: showEstimate,
    gstApplicable, documentName: 'Sales Order',
  });

  // For the Open customer link's "Leave without saving?": what Save would
  // send, as last loaded, compared with what it would send now.
  const [savedPayload, setSavedPayload] = useState(null);
  const [snapshotDue, setSnapshotDue] = useState(false);
  useEffect(() => {
    if (!snapshotDue) return;
    setSavedPayload(JSON.stringify(buildPayload()));
    settleInputsKey(currentInputsKey);
    setSnapshotDue(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotDue]);
  const hasUnsavedChanges = () => canSave && (!id || JSON.stringify(buildPayload()) !== savedPayload);

  // Freight changed on an order the Customer has Submitted: the Customer is
  // told on the order — Save adds a line to the Comment to Customer
  // (salesOrderController.js's freightChangeNote) — so staff confirm first.
  // Adding the first Shipping charge to an order with none, its Options
  // unchanged (a portal order's shipping "to be added", issue #351), isn't
  // a change: no line, no confirmation (firstShippingCharge). cost is the
  // charge Save will send, a re-estimated one included.
  const confirmFreightChange = async (cost = shippingCost) => {
    if (!submitted) return true;
    if (orderMeta.savedShippingBlank && orderMeta.savedOptionsFreight === freightTotal(null, selectedOptions)) return true;
    const before = orderMeta.savedFreight;
    const after = freightTotal(cost, selectedOptions);
    if (before === after) return true;
    const describe = (value) => (value === null ? 'to be added' : `${formatMoney(value)} ex GST`);
    return await askConfirm(`Freight changes from ${describe(before)} to ${describe(after)}. The Customer has already Submitted this order, so a line saying so will be added to the Comment to Customer, shown in the portal and on the print. Continue?`, { confirmLabel: 'Change Freight' });
  };

  // Save stays on the order (a new one moves onto its own page) so a long
  // order can be saved as it's built; Save & Close (close true) goes back
  // to the list. saveMode says which is running, for its spinner. Each
  // asks first whether to re-estimate a changed order's shipping (#437);
  // reestimated is what that adds to the payload.
  const saveKey = useSaveKey();
  const [saveMode, setSaveMode] = useState(null);
  const handleSave = async (close) => {
    if (!customerId || !canSave) return;
    const discountProblem = discount.problem(totals.linesAmount);
    if (discountProblem) {
      showError(undefined, discountProblem);
      return;
    }
    const reestimated = await confirmReestimate();
    if (!reestimated) return;
    if (id && !(await confirmFreightChange(reestimated.shipping_cost ?? shippingCost))) return;
    setSaving(true);
    setSaveMode(close ? 'close' : 'stay');
    try {
      const payload = { ...buildPayload(), ...reestimated };
      if (id) {
        await axios.put(`/api/sales-orders/${id}`, payload);
      } else {
        const res = await axios.post('/api/sales-orders', { ...payload, order_date: newOrderDate, save_key: saveKey });
        if (!close) {
          dismissError();
          navigate(`/sales-orders/${res.data.data.id}`, { replace: true });
          return;
        }
      }
      dismissError();
      if (close) {
        navigate('/sales-orders');
        return;
      }
      await loadOrder();
      setHistoryVersion((v) => v + 1);
    } catch (err) {
      showError(err, 'Failed to save Sales Order');
    } finally {
      setSaving(false);
      setSaveMode(null);
    }
  };

  // Cancel leaves without saving, checking first if anything's changed.
  const handleCancel = async () => {
    const unsaved = id ? hasUnsavedChanges() : !readOnly && (!!customerId || lines.length > 0);
    if (unsaved && !(await askConfirm('Leave without saving? Your changes will be lost.', { confirmLabel: 'Leave', destructive: true }))) return;
    navigate('/sales-orders');
  };

  // Estimate Shipping (issue #351), the same as QuotePage.js's: prices the
  // order as it is on screen and fills the Shipping charge with the
  // estimate. A charge staff typed over is only replaced if they say so.
  const handleEstimate = async () => {
    if (typedOver && !(await askConfirm('The Shipping charge was typed in. Re-estimating replaces it with the new estimate. Continue?', { confirmLabel: 'Replace Charge' }))) return;
    setEstimating(true);
    setEstimateError('');
    try {
      showEstimate(await requestEstimate(estimatePackagingId), estimatePackagingId);
    } catch (err) {
      setEstimateError(err.response?.data?.message || 'Couldn’t estimate shipping');
    }
    setEstimating(false);
  };

  // Selecting another Australia Post method's price (as QuotePage.js's).
  const handleSelectMethod = async (key) => {
    const method = (shippingEstimate?.methods || []).find((m) => m.key === key && m.ok);
    if (!method || shippingEstimate.selected_method === key) return;
    if (typedOver && !(await askConfirm(`The Shipping charge was typed in. Selecting ${method.name} replaces it with its price. Continue?`, { confirmLabel: 'Replace Charge' }))) return;
    setShippingEstimate({
      ...shippingEstimate,
      selected_method: key,
      postage: method.postage,
      options: method.options,
      options_cost: method.options_cost,
      rounding: method.rounding,
      total: method.total,
      charge: method.charge,
      note: method.note,
    });
    setShippingCost(Number(method.charge));
    setShippingOverrideReason('');
  };

  // Submit and Accept Order each Save first, so whatever's on screen is
  // what gets committed and checked, then move the order on. Submit records
  // the Customer's commitment on their behalf; Accept Order records the
  // Supplier's and releases it for picking (the server names anything
  // missing). historyVersion remounts Status History so the new step shows
  // straight away.
  const [historyVersion, setHistoryVersion] = useState(0);
  const [moving, setMoving] = useState(false);
  const saveThen = async (step, failure, reestimated = {}) => {
    setMoving(true);
    try {
      await axios.put(`/api/sales-orders/${id}`, { ...buildPayload(), ...reestimated });
      await axios.post(`/api/sales-orders/${id}/${step}`);
      await loadOrder();
      setHistoryVersion((v) => v + 1);
      dismissError();
    } catch (err) {
      showError(err, failure);
      // The Save may have gone through even if the step was refused.
      await loadOrder().catch(() => {});
    }
    setMoving(false);
  };

  const handleSubmit = async () => {
    const reestimated = await confirmReestimate();
    if (!reestimated) return;
    const message = orderMeta.sourceQuote
      ? `Submit ${orderMeta.display_number} on the Customer's behalf? This records that the Customer has accepted the order as it stands. Its Customer, lines, quantities and prices then lock again; shipping stays editable until it ships.`
      : `Submit ${orderMeta.display_number} for the Customer? This records their order and holds its prices. Lines, quantities, overrides and shipping stay editable until it ships.`;
    if (!(await askConfirm(message, { confirmLabel: 'Submit' }))) return;
    saveThen('submit', 'Failed to Submit Sales Order', reestimated);
  };

  // Payments (issue #338). Accepting a Prepay Customer's order that isn't
  // fully paid warns with the balance owing; staff can still go ahead.
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const confirmBalanceOwing = async () => {
    if (!paymentSummary?.is_prepay) return true;
    let latest = paymentSummary;
    try {
      latest = await fetchPayments('sales-orders', id);
    } catch (err) {
      // Fall back to the figures already on screen.
    }
    if (!latest?.is_prepay || latest.balance_owing <= 0.005) return true;
    return await askConfirm(`${orderMeta.display_number} isn't fully paid — the balance owing is ${formatPrice(latest.balance_owing)} of ${formatPrice(latest.total)}. Accept it anyway?`, { confirmLabel: 'Accept Anyway' });
  };

  // What Accept refuses outright, as the screen stands: one sentence per
  // blocker, empty when none. Mirrors the server's acceptanceMissing
  // (salesOrderController.js) and Not linked to QuickBooks (issue #268),
  // which still re-check on Accept.
  const acceptBlockers = () => {
    const missing = [];
    if (!lines.length) missing.push('a line');
    if (!addressId) missing.push('a delivery address');
    const serviceCarrierId = selectedShippingService ? (selectedShippingService.supplier?.id ?? selectedShippingService.supplier_id ?? null) : null;
    if (!carrierId && (!selectedShippingService || serviceCarrierId != null)) missing.push('a Carrier');
    if (!shippingServiceId) missing.push('a Shipping Service');
    const blockers = [];
    if (missing.length) {
      const needs = missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`;
      blockers.push(`it needs ${needs}`);
    }
    if (quickbooksUnlinked) {
      const names = [quickbooksUnlinked.customer?.name, ...quickbooksUnlinked.products.map((p) => p.sku)].filter(Boolean).join(', ');
      blockers.push(`not linked to QuickBooks: ${names} (marked on the order)`);
    }
    return blockers;
  };

  const handleAccept = async () => {
    // Blockers first, before any warning (ui-architecture.md "Blockers
    // Before Warnings"): staff never answer prompts for an Accept that
    // can't go ahead.
    const blockers = acceptBlockers();
    if (blockers.length) {
      showError(undefined, `Unable to Accept ${orderMeta.display_number} — ${blockers.join('; ')}. Fix ${blockers.length === 1 ? 'it' : 'them'}, then Accept again.`);
      return;
    }
    if (!(await confirmBalanceOwing())) return;
    // No shipping charge yet (issue #351): staff can stop to add it, or
    // carry on.
    if (shippingNotAdded(selectedShippingService, shippingCost)
      && !(await askConfirm(`Shipping hasn’t been added to ${orderMeta.display_number}. Accept it anyway?`, { confirmLabel: 'Accept Anyway' }))) return;
    const reestimated = await confirmReestimate();
    if (!reestimated) return;
    if (!(await confirmFreightChange(reestimated.shipping_cost ?? shippingCost))) return;
    const stillEditable = orderMeta.sourceQuote
      ? 'Shipping stays editable until it ships.'
      : 'Lines, quantities, prices and shipping stay editable until it ships.';
    if (!(await askConfirm(`Accept ${orderMeta.display_number}? This records your commitment and releases it for picking. ${stillEditable}`, { confirmLabel: 'Accept' }))) return;
    saveThen('accept', 'Failed to Accept Sales Order', reestimated);
  };

  // Revise (a Quote-sourced order only): back to Draft so its lines can
  // change. Saves first, like Submit and Accept.
  const handleRevise = async () => {
    const reestimated = await confirmReestimate();
    if (!reestimated) return;
    if (!(await askConfirm(`Revise ${orderMeta.display_number}? It returns to Draft so its lines, quantities and prices can change. Submit it again once the Customer has accepted the changes.`, { confirmLabel: 'Revise' }))) return;
    saveThen('revise', 'Failed to Revise Sales Order', reestimated);
  };

  const handleWithdrawAcceptance = async () => {
    if (!(await askConfirm(`Withdraw acceptance of ${orderMeta.display_number}? It returns to Submitted and can't be picked until it's Accepted again. The Customer's order and its prices stand.`, { confirmLabel: 'Withdraw Acceptance' }))) return;
    setMoving(true);
    try {
      await axios.post(`/api/sales-orders/${id}/withdraw-acceptance`);
      await loadOrder();
      setHistoryVersion((v) => v + 1);
      dismissError();
    } catch (err) {
      showError(err, 'Failed to withdraw acceptance');
    }
    setMoving(false);
  };

  const handleDelete = async () => {
    if (!(await askConfirm(`Delete ${orderMeta.display_number}? The order and its lines are removed and its number isn't reused. This cannot be undone.`, { confirmLabel: 'Delete', destructive: true }))) return;
    setMoving(true);
    try {
      await axios.delete(`/api/sales-orders/${id}`);
      dismissError();
      navigate('/sales-orders');
    } catch (err) {
      showError(err, 'Failed to delete Sales Order');
      setMoving(false);
    }
  };

  // This Order's own shipments on the cross-order Shipments screen — the
  // View Shipments action and each Shipped entry in Status History go there.
  const shipmentsLink = `/warehouse-shipments?order_type=sales_order&order_id=${id}&order_number=${encodeURIComponent(orderMeta?.display_number || '')}`;
  // Two groups (ui-architecture.md's Actions Menu Grouping): first the
  // workflow step(s) open at the order's current status, in lifecycle
  // order, then — under a divider — the actions offered regardless of
  // status. Delete sits last behind its own divider. The step the order is
  // at (Submit, Accept Order, Pick, Pack, Ship, Release Hold) is the
  // header's Next button (SalesOrderNextButton); the menu keeps the rest.
  const workflowItems = [];
  // Ship whatever's packed and not yet shipped (ShipConfirmDialog.js) —
  // here as well as the Next button, since a part shipment can go while the
  // order's still at Pick or Pack, or On Hold.
  if (id && canShip) {
    workflowItems.push({
      key: 'ship',
      needs: 'update',
      label: 'Ship',
      onSelect: openShipDialog,
    });
  }
  // Revise: a Quote-sourced order, Submitted or Accepted with nothing
  // picked yet (the server refuses otherwise).
  if (id && orderMeta?.sourceQuote && ['submitted', 'processing'].includes(orderMeta.status)) {
    workflowItems.push({
      key: 'revise',
      needs: 'update',
      label: moving ? 'Revising...' : 'Revise',
      onSelect: handleRevise,
      disabled: moving || saving,
    });
  }
  // Only while nothing's been picked, packed or shipped — the order-level
  // status stays Accepted (stored 'processing') until the first pick.
  if (id && orderMeta?.status === 'processing') {
    workflowItems.push({
      key: 'withdraw-acceptance',
      needs: 'update',
      label: 'Withdraw Acceptance',
      onSelect: handleWithdrawAcceptance,
      disabled: moving,
    });
  }

  // A Prepay Customer's payment (issue #338), at any status.
  if (id && paymentSummary?.can_record) {
    workflowItems.push({
      key: 'record-payment',
      needs: 'update',
      label: 'Record Payment',
      onSelect: () => setRecordPaymentOpen(true),
    });
  }

  const standingItems = [
    // Priced Sales Order print (issue #293) — the Customer's copy, at every
    // status.
    {
      key: 'print-sales-order',
      label: 'Print Sales Order',
      onSelect: () => setShowReport('sales-order'),
      disabled: !id,
      disabledTitle: !id ? 'Save the Sales Order first' : undefined,
    },
    // Pick Slip (issue #295) — at every status once saved, so a Draft can
    // be picked and weighed to settle its shipping cost before it's
    // Submitted/Accepted. Stays available through to fully Shipped for
    // reprints.
    {
      key: 'print-pick-slip',
      label: 'Print Pick Slip',
      onSelect: () => setShowReport('pick-slip'),
      disabled: !id,
      disabledTitle: !id ? 'Save the Sales Order first' : undefined,
    },
  ];
  if (id) {
    // Jumps to the cross-order Shipments screen (ShipmentsTable.js),
    // pre-filtered to this Order's own shipments — same query-string shape
    // as any other "In Use" drill-through (ui-architecture.md).
    standingItems.push({
      key: 'view-shipments',
      label: 'View Shipments',
      onSelect: () => navigate(shipmentsLink),
    });
  }
  // Who changed this order and what changed (issue #391).
  if (id && orderMeta && accessFor('global-audit').read) {
    standingItems.push({
      key: 'audit-log',
      label: 'Audit Log',
      onSelect: () => navigate(`/global-audit?${new URLSearchParams({ document_type: 'sales_order', document_id: id, number: orderMeta.display_number || '' })}`),
    });
  }
  if (id && orderMeta && !pricesHeld) {
    standingItems.push({
      key: 'refresh-order',
      needs: 'update',
      label: refreshingOrder ? 'Refreshing...' : 'Refresh Sales Order',
      onSelect: handleRefreshOrder,
      disabled: refreshingOrder,
    });
  }
  if (id && orderMeta) {
    standingItems.push(orderMeta.onHold
      ? { key: 'release-hold', label: 'Release Hold', needs: 'update', onSelect: handleReleaseHold }
      : { key: 'hold', label: 'On Hold', needs: 'update', onSelect: handleHold });
  }

  const actionsMenuItems = [...workflowItems, { key: 'divider-workflow', divider: true }, ...standingItems];
  // Only a Draft staff raised directly — see salesOrderController.js's
  // deleteSalesOrder for why converted and portal orders are excluded.
  if (id && orderMeta?.status === 'draft' && !orderMeta.sourceQuote && !orderMeta.portalLogin) {
    actionsMenuItems.push({ key: 'divider-delete', divider: true }, {
      key: 'delete',
      needs: 'delete',
      label: 'Delete',
      onSelect: handleDelete,
      disabled: moving || saving,
    });
  }
  // The header Next button's handler for each step, confirmations
  // included; it shows only the one for the step the order is at.
  const fulfilmentState = { state: { orderType: 'sales_order', orderId: Number(id) } };
  const stepperActions = {
    pick: { onSelect: () => navigate('/warehouse-pick', fulfilmentState) },
    pack: { onSelect: () => navigate('/warehouse-pack', fulfilmentState) },
    // The same Ship dialog as the green Ship button whenever that's offered,
    // otherwise the order on the Ship screen.
    ship: { onSelect: id && canShip && canUpdate ? openShipDialog : () => navigate('/warehouse-ship', fulfilmentState) },
    ...(canUpdate ? {
      draft: { onSelect: handleSubmit, disabled: moving || saving },
      accepted: { onSelect: handleAccept, disabled: moving || saving },
      hold: { onSelect: handleReleaseHold },
    } : {}),
  };
  const stepperOrder = orderMeta && {
    status: orderMeta.status,
    on_hold: orderMeta.onHold,
    on_hold_reason: orderMeta.onHoldReason,
    lines: lines.map((l) => ({ qty: l.qty, qty_cancelled: l.qtyCancelled, qty_picked: l.qtyPicked, qty_packed: l.qtyPacked, qty_shipped: l.qtyShipped })),
  };
  if (loading) {
    return <div style={{ margin: '32px 24px', textAlign: 'center', color: '#888' }}>Loading...</div>;
  }
  if (notFound) {
    return <div style={{ margin: '32px 24px', textAlign: 'center', color: '#888' }}>Sales Order not found</div>;
  }

  // Form Section Cards — see ui-architecture.md.
  const sectionHeaderStyle = { margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 };
  const sectionStyle = { padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };

  return (
    <div className="detail-timeline-row" style={{ width: '100%', maxWidth: 1450, margin: '32px auto 48px' }}>
    <div className="detail-timeline-main" style={{ background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <ShippedNotice shipment={shippedShipment} onClose={() => setShippedShipment(null)} />
      <Accordion
        title={(
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, fontSize: 18, color: '#1a1a1a' }}>
            {orderMeta ? `Sales Order ${orderMeta.display_number}` : 'New Sales Order'}
            {orderMeta && customer?.label && <span style={{ fontWeight: 400, fontSize: 15, color: '#555' }}>· {customer.label}</span>}
            {orderMeta?.orderDate && <span style={{ fontWeight: 400, fontSize: 15, color: '#555' }}>· {formatDate(orderMeta.orderDate)}</span>}
            {orderMeta && <PaymentStatusPill status={paymentSummary?.payment_status} />}
          </span>
        )}
        // The progress stepper (issue #310) in place of a status pill, the
        // Next button for the step the order is at, and the page's one
        // Actions menu — in headerExtra, since they're interactive and the
        // title is a button.
        className="doc-header"
        headerExtra={(
          <>
            {stepperOrder && <SalesOrderStepper order={stepperOrder} />}
            {stepperOrder && <SalesOrderNextButton order={stepperOrder} actions={stepperActions} />}
            <ActionsMenu style={{ marginLeft: 8 }} items={actionsMenuItems} placement="down" />
          </>
        )}
        showMenuPlaceholder={false}
        // Open on a new document (Customer, delivery and Shipping come
        // first); collapsed on an existing one, so the lines lead.
        defaultOpen={!id}
      >
      {orderMeta?.sourceQuote && justConverted && (
        <ConvertedBanner
          title="Converted from a Quote"
          message={`This Sales Order was created by converting an Accepted Quote. Prices were copied exactly as accepted and were not re-priced. ${acceptMissing
            ? `It couldn't be Accepted yet: it needs ${acceptMissing}. Add it, then Accept Order.`
            : acceptUnlinked
            ? `It couldn't be Accepted yet. Not linked to QuickBooks: ${acceptUnlinked}. Link them, then Accept Order.`
            : "It's Accepted and ready to pick."}`}
          linkTo={`/quotes/${orderMeta.sourceQuote.id}`}
          linkLabel={`View Quote ${orderMeta.sourceQuote.display_number}`}
        />
      )}
      {/* A Quote-sourced order past Draft: what's locked and how to change it. */}
      {linesLocked && !readOnly && (
        <div style={{ background: '#eef5fd', border: '1px solid #c5dcf5', borderRadius: 8, padding: '12px 16px', marginBottom: 18, fontSize: 14, color: '#1f2d3d' }}>
          <strong>From Quote {orderMeta.sourceQuote?.display_number}</strong> — the Customer, lines, quantities and prices are as the Customer accepted them. Shipping stays editable.
          {['submitted', 'processing'].includes(orderMeta.status)
            ? ' To change anything else, choose Revise.'
            : ' Lines can no longer change now picking has started.'}
        </div>
      )}
      {/* A portal order (issue #284): who submitted it and their note. */}
      {orderMeta?.portalLogin && (
        <div style={{ background: '#eef5fd', border: '1px solid #c5dcf5', borderRadius: 8, padding: '12px 16px', marginBottom: 18, fontSize: 14, color: '#1f2d3d' }}>
          <strong>Portal order</strong> — submitted from the customer portal by {orderMeta.portalLogin}
          {orderMeta.customerNote && (
            <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}><span style={{ color: '#5a6b85' }}>Note from the customer:</span> {orderMeta.customerNote}</div>
          )}
        </div>
      )}
      {orderMeta?.onHold && (
        <div style={{ background: '#fff8e1', border: '1px solid #f0c34d', borderRadius: 8, padding: '12px 16px', marginBottom: 18, fontSize: 14, color: '#7a5c00' }}>
          <strong>On Hold</strong>{orderMeta.onHoldReason ? ` — ${orderMeta.onHoldReason}` : ''}
        </div>
      )}
      <div style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
        {orderMeta ? (
          <>Origin {orderMeta.originLabel || '—'}{' · '}Created by {orderMeta.createdByName || '—'}</>
        ) : 'Not yet saved'}
        {customerTax && (
          <>{' · '}Tax Handling {taxHandling?.name || '—'}</>
        )}
      </div>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={sectionStyle}>
          <h4 style={sectionHeaderStyle}>Order Details</h4>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Date</label>
              {isOrderDateEditable ? (
                <DateInput
                  className="styled-input"
                  value={orderDate || ''}
                  onChange={handleOrderDateChange}
                  disabled={savingOrderDate}
                  style={{ fontSize: 14, width: 160 }}
                />
              ) : (
                <div
                  style={{ padding: '6px 0', fontSize: 14 }}
                  title="This Sales Order can no longer have its date changed — a line has already shipped."
                >
                  {formatDate(orderDate)}
                </div>
              )}
            </div>

            {orderMeta?.completedDate ? (
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Completed</label>
                <div style={{ padding: '6px 0', fontSize: 14 }} title="The day the last line was shipped in full or closed short">
                  {formatDate(orderMeta.completedDate)}
                </div>
              </div>
            ) : orderMeta?.fulfilledDate && (
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Last Shipped</label>
                <div style={{ padding: '6px 0', fontSize: 14 }} title="The latest Date Shipped among this order's shipments">
                  {formatDate(orderMeta.fulfilledDate)}
                </div>
              </div>
            )}

            <div style={{ position: 'relative', minWidth: 300 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Customer<RequiredMark /></label>
              <RecordLookupField
                value={customer}
                search={searchCustomers}
                optionLabel={customerLabel}
                onSelect={handleSelectCustomer}
                href={customer ? `/commercial-entities/${customer.id}` : undefined}
                icon={customer?.type === 'individual' ? UserRound : Building2}
                readOnly={linesReadOnly}
                placeholder="Search code or name..."
                origin={{ label: orderMeta ? orderMeta.display_number : 'New Sales Order', path: id ? `/sales-orders/${id}` : '/sales-orders/new' }}
                noun="Sales Order"
                hasUnsavedChanges={hasUnsavedChanges}
                openTitle="Open customer"
                changeTitle="Change customer"
              />
              {quickbooksUnlinked?.customer && (
                <div style={{ marginTop: 4 }}>
                  <QuickBooksLinkPill noun="Customer" id={quickbooksUnlinked.customer.id} reason={quickbooksUnlinked.customer.reason} />
                </div>
              )}
            </div>

            <div style={{ minWidth: 260, width: 260 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Address</label>
              <AddressPicker
                addresses={customerAddresses}
                value={addressId}
                onChange={setAddressId}
                disabled={!customerId || customerAddresses.length === 0 || readOnly}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Payment Terms</label>
              <div style={{ padding: '6px 0', fontSize: 14 }}>
                {customerTax?.payment_term?.name || '—'}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Purchase Order Reference</label>
              <input
                className="styled-input"
                placeholder="Customer's PO number"
                value={poReference}
                onChange={(e) => setPoReference(e.target.value)}
                disabled={readOnly}
                style={{ fontSize: 14, width: 200 }}
              />
            </div>
          </div>
        </div>

        <div style={sectionStyle}>
          <h4 style={sectionHeaderStyle}>Shipping</h4>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Carrier</label>
              <select
                className="styled-input"
                value={carrierId}
                onChange={(e) => { setCarrierId(e.target.value); setShippingServiceId(''); }}
                disabled={readOnly}
                style={{ fontSize: 14, width: 200 }}
              >
                <option value="">(none)</option>
                {carrierChoices(shippingServices, carrierId).map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Shipping Service</label>
              <select
                className="styled-input"
                value={shippingServiceId}
                onChange={(e) => { setCarrierId(carrierForService(shippingServices, e.target.value, carrierId)); setShippingServiceId(e.target.value); }}
                disabled={readOnly}
                style={{ fontSize: 14, width: 200 }}
              >
                <option value="">(none)</option>
                {servicesForCarrier(shippingServices, carrierId, shippingServiceId).map((sm) => (
                  <option key={sm.id} value={sm.id}>{sm.description}</option>
                ))}
              </select>
              <ShippingOptionsPicker
                shippingServiceId={shippingServiceId}
                quoteSubtotalExGst={subtotal}
                lines={lines}
                gstApplicable={gstApplicable}
                gstRate={gstRate}
                locked={readOnly || chargeLocked}
                selectedOptions={selectedOptions}
                onChange={setSelectedOptions}
              />
            </div>

          </div>
        </div>
      </div>
      {canEstimate && (
        <QuoteShippingEstimate
          packagings={packagings}
          packagingId={estimatePackagingId}
          onPackagingChange={setEstimatePackagingId}
          estimate={estimate}
          typedOver={typedOver}
          selectedOptions={selectedOptions}
          onEstimate={handleEstimate}
          onSelectMethod={handleSelectMethod}
          estimating={estimating}
          error={estimateError}
          locked={readOnly || chargeLocked}
          gstApplicable={gstApplicable}
          viewInc={viewInc}
          gstRate={gstRate}
        />
      )}
      </Accordion>

      <div style={{ marginTop: 24 }}>
      <Accordion
        title={`Lines (${lines.length})`}
        showMenuPlaceholder={false}
        headerExtra={(
          <span style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14 }}>
            {anyNeedsPrice && <span style={{ fontSize: 12, color: '#b45309' }}>One or more lines need a price</span>}
            {(gstApplicable || !basis.retail) && <span>Subtotal{basisSuffix} {formatPrice(viewSubtotal(amountLines, viewInc))}</span>}
            {gstApplicable && <SegmentedToggle options={GST_VIEWS} value={viewInc ? 'inc' : 'ex'} onChange={setGstView} />}
          </span>
        )}
      >
        {lines.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <LineOrderSwitch value={lineOrder} onChange={setLineOrder} />
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '5%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '28%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '5%' }} />
            <col style={{ width: '5%' }} />
            <col style={{ width: '5%' }} />
            <col style={{ width: '4%' }} />
          </colgroup>
          <thead style={{ fontSize: 13 }}>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #b3bbc7' }}>
              <th style={{ padding: '6px 10px 6px 0', color: '#888', textAlign: 'right' }}>#</th>
              <th style={{ padding: '6px 8px 6px 0' }}>SKU</th>
              <th style={{ padding: '6px 8px 6px 0' }}>Product</th>
              <th style={{ padding: '6px 8px 6px 0' }}>UoM</th>
              <th style={{ padding: '6px 18px 6px 0', textAlign: 'right' }}>Qty</th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Unit Price{basisLine}</th>
              <th style={{ padding: '6px 22px 6px 0', textAlign: 'right' }}>Amount{basisLine}</th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Pick</th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Pack</th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Ship</th>
              <th style={{ padding: '6px 8px 6px 0', width: 20 }} />
            </tr>
          </thead>
          <tbody>
            {shownLines.map((line) => {
              // A line whose entire qty was cancelled with nothing ever
              // shipped reads as struck-through rather than removed from the
              // Order — the customer did ask for it originally, so the record
              // stays (see cancelLineBackorder); it just shows as no longer
              // outstanding. A partly-shipped line that later gets its
              // remainder cancelled keeps its plain text, since some of it
              // did go out.
              const fullyCancelled = Number(line.qtyCancelled) > 0 && Number(line.qtyShipped) === 0 && outstandingQty(line) === 0;
              return (
              <tr key={line.key} className="list-row" {...rowProps(line, { verticalAlign: 'top' })}>
                <td style={{ padding: '6px 10px 6px 0', color: '#888', textAlign: 'right' }}>{handle(line)}{lines.indexOf(line) + 1}</td>
                <td style={{ padding: '6px 8px 6px 0', textDecoration: fullyCancelled ? 'line-through' : 'none', color: fullyCancelled ? '#999' : 'inherit' }}>
                  {line.product?.sku}
                  {lineWeight(line) === null && (
                    <span title="Missing Weight" style={{ display: 'inline-flex', cursor: 'help', marginLeft: 3, verticalAlign: -1 }}>
                      <Info size={13} strokeWidth={2.5} style={{ color: '#b45309' }} />
                    </span>
                  )}
                </td>
                <td style={{ padding: '6px 8px 6px 0', textDecoration: fullyCancelled ? 'line-through' : 'none', color: fullyCancelled ? '#999' : 'inherit' }}>
                  {line.product?.name}
                  {unlinkedReasonFor(line) && (
                    <>
                      {' '}
                      <QuickBooksLinkPill noun="Product" id={line.productId} reason={unlinkedReasonFor(line)} />
                    </>
                  )}
                  {line.product?.availability_badge && (
                    <>
                      {' '}
                      <AvailabilityPill
                        badge={line.product.availability_badge}
                        date={line.product.availability_date}
                        color={line.product.availability_badge_color}
                      />
                    </>
                  )}
                </td>
                <td style={{ padding: '6px 8px 6px 0' }}>
                  <select
                    className="styled-input"
                    value={line.uomId}
                    onChange={(e) => handleLineUomChange(line.key, e.target.value)}
                    disabled={linesReadOnly}
                    style={{ fontSize: 12, width: '100%', minWidth: 0 }}
                  >
                    {line.uomOptions.map((pu) => (
                      <option key={pu.uom_id} value={pu.uom_id}>{pu.name}</option>
                    ))}
                  </select>
                  {(() => {
                    const selectedUom = line.uomOptions.find((pu) => String(pu.uom_id) === String(line.uomId));
                    const baseUom = (line.allUomOptions || line.uomOptions).find((pu) => pu.is_base);
                    if (!selectedUom || !baseUom || selectedUom.is_base) return null;
                    const baseQty = (Number(line.qty) || 0) * Number(selectedUom.conversion_rate || 1);
                    return (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2, whiteSpace: 'nowrap' }}>
                        {formatNumber(baseQty, { maxDecimals: 2 })} {baseUom.code || baseUom.name}
                      </div>
                    );
                  })()}
                </td>
                <td style={{ padding: '6px 18px 6px 0', textAlign: 'right' }}>
                  <NumberInput
                    min={1}
                    maxDecimals={4}
                    stepper
                    value={line.qty}
                    onChange={(e) => handleLineQtyChange(line.key, e.target.value)}
                    onBlur={() => handleLineQtyBlur(line.key)}
                    disabled={linesReadOnly}
                    style={{ width: '100%', minWidth: 0, fontSize: 12, textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>
                  {linesReadOnly || offBasis ? (
                    <div style={{ fontSize: 13 }}>
                      {formatUnitPrice(linePrice(line))}
                      {baseUnitPriceLabel(line) && (
                        <div style={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap' }}>{baseUnitPriceLabel(line)}</div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                        <LineUnitPriceField price={linePrice(line)} onCommit={(val) => handleOverridePrice(line.key, val)} />
                        <PriceCalculator initialValue={linePrice(line)} onResult={(val) => handleOverridePrice(line.key, val)} />
                        {line.overridden && (
                          <OverrideRevertBadge
                            onClick={() => handleRevertOverride(line.key)}
                            title="Overridden — click to restore the system price"
                          />
                        )}
                      </div>
                      {baseUnitPriceLabel(line) && (
                        <div style={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap' }}>{baseUnitPriceLabel(line)}</div>
                      )}
                      {line.overridden && (
                        <input
                          className="styled-input"
                          type="text"
                          placeholder="Reason (optional)"
                          value={line.overrideReason || ''}
                          onChange={(e) => handleOverrideReasonChange(line.key, e.target.value)}
                          style={{ width: 140, fontSize: 11, padding: '2px 6px', height: 22 }}
                        />
                      )}
                    </div>
                  )}
                </td>
                <td style={{ padding: '6px 22px 6px 0', textAlign: 'right' }}>
                  {formatPrice(lineAmount(line))}
                </td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', fontSize: 12, color: '#666' }}>
                  {line.qtyPicked !== undefined && pickPackCount(line.qtyPicked, line.qty)}
                </td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', fontSize: 12, color: '#666' }}>
                  {line.qtyPacked !== undefined && pickPackCount(line.qtyPacked, line.qty)}
                </td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', fontSize: 12, color: '#666' }}>
                  {line.qtyShipped !== undefined && pickPackCount(line.qtyShipped, line.qty)}
                  {line.backordered && (
                    <div style={{ marginTop: 2 }}><CategoryPill value="Backordered" /></div>
                  )}
                  {line.backordered && !readOnly && (
                    <div style={{ marginTop: 2 }}>
                      <button
                        type="button"
                        onClick={() => handleOpenCancelDialog(line)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, color: '#1976d2', textDecoration: 'underline' }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  {Number(line.qtyCancelled) > 0 && (
                    <div style={{ marginTop: 2, fontSize: 11, color: '#999' }}>{line.qtyCancelled} cancelled</div>
                  )}
                </td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>
                  {!linesReadOnly && (
                    <button type="button" onClick={() => handleRemoveLine(line.key)} title="Remove line" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#b91c1c' }}>×</button>
                  )}
                </td>
              </tr>
              );
            })}

            {!linesReadOnly && <tr>
              <td style={{ padding: '6px 10px 6px 0', color: '#888', textAlign: 'right' }}>{lines.length + 1}</td>
              <td colSpan={2} style={{ padding: '6px 8px 6px 0', position: 'relative' }}>
                <input
                  className="styled-input"
                  placeholder={customerId ? 'Search name or SKU...' : 'Select a Customer first'}
                  disabled={!customerId}
                  value={draftRow.productSearch}
                  onChange={(e) => handleDraftSearchChange(e.target.value)}
                  onFocus={() => { if (draftRow.results.length) setDraftRow((prev) => ({ ...prev, showResults: true })); }}
                  onBlur={() => setTimeout(() => setDraftRow((prev) => ({ ...prev, showResults: false })), 150)}
                  style={{ fontSize: 12, width: '100%' }}
                />
                {draftRow.showResults && draftRow.results.length > 0 && (
                  <ul style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, width: 320, margin: 0, padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                    {draftRow.results.map((p) => (
                      <li
                        key={p.id}
                        onMouseDown={() => handleDraftSelectProduct(p)}
                        style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13, borderRadius: 4 }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#e3eaf3'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {productLabel(p)}
                      </li>
                    ))}
                  </ul>
                )}
              </td>
              <td colSpan={6} />
            </tr>}
          </tbody>
        </table>
      </Accordion>
      </div>

      {/* Comments and Totals start collapsed to keep the lines in view;
          the header still shows the Total, ex or inc GST following the
          switch like the Lines header's Subtotal. */}
      <div>
      <Accordion
        title="Comments & Totals"
        defaultOpen={false}
        showMenuPlaceholder={false}
        headerExtra={(
          <span style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14 }}>
            <span style={{ fontWeight: 700 }}>Total{basisSuffix} {formatPrice(gstApplicable && !viewInc ? Math.round((totals.total - totals.gst) * 100) / 100 : totals.total)}</span>
          </span>
        )}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 72 }}>
          <div style={{ flex: 1 }}>
            <DocumentComments
              customerComment={customerComment}
              onCustomerCommentChange={setCustomerComment}
              staffComment={staffComment}
              onStaffCommentChange={setStaffComment}
              customerHint="Shown to the customer in the portal and on the print"
              disabled={!canSave}
            />
          </div>
          {/* While editable, the totals keep a 20px column on the right for
              the Shipping charge's revert badge (#437), so every figure
              stays lined up whether or not the badge shows. */}
          <div style={{ minWidth: 240, paddingRight: !costFieldLocked ? 20 : 0 }}>
            {anyNeedsPrice && (
              <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8, textAlign: 'right' }}>
                One or more lines need a price
              </div>
            )}
            {/* Trailing hidden spacer matches GstAwareCostField's calculator-
                button width+gap (15px + 4px) below, so Subtotal/GST/Total's
                own right-aligned figures land in the same column as
                Shipping's typed value rather than sitting 19px further right
                (where its calculator button pushes them). Skipped once the
                form is read-only — a locked field has no calculator button
                to align against. */}
            {/* Subtotal (ex or inc GST, following the switch), Shipping,
                Options, GST, Total (inc GST) — the same for Business and
                Retail. Retail without GST shows only the Total. */}
            {(gstApplicable || !basis.retail) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0' }}>
                <span>Subtotal{basisSuffix}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {formatPrice(viewSubtotal(amountLines, viewInc))}
                  {!costFieldLocked && <span aria-hidden="true" style={{ width: 15, visibility: 'hidden' }} />}
                </span>
              </div>
            )}
            {/* Fixed once it's on an Invoice, the same as the shipping charge. */}
            <OrderDiscountRow
              discount={discount}
              amount={viewDiscount(amountLines, totals.discount, basis.retail, viewInc)}
              basisSuffix={basisSuffix}
              entryBasis={gstApplicable ? (basis.retail ? 'inc GST' : 'ex GST') : ''}
              locked={costFieldLocked}
              gutter={costFieldLocked ? 0 : 19}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, padding: '4px 0', gap: 8 }}>
              <span>Shipping{basisSuffix}</span>
              {/* The revert badge sits in the totals' right-hand column, so the charge
                  still lines up with the amounts above and below. */}
              <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <GstAwareCostField
                  value={shippingCost}
                  onCommit={setShippingCost}
                  gstApplicable={viewInc}
                  gstRate={gstRate}
                  locked={costFieldLocked}
                />
                {typedOver && !costFieldLocked && (
                  <span style={{ position: 'absolute', left: '100%', marginLeft: 4, display: 'flex' }}>
                    <OverrideRevertBadge
                      onClick={() => { setShippingCost(Number(estimate.charge)); setShippingOverrideReason(''); }}
                      title="Overridden — click to restore the estimate"
                    />
                  </span>
                )}
              </span>
            </div>
            {/* Once invoiced the charge is fixed, so only "Invoiced" shows. */}
            {estimate && !chargeLocked && (
              <div style={{ fontSize: 11, color: '#5a6b85', textAlign: 'right', marginTop: -2, marginBottom: 4, paddingRight: costFieldLocked ? 0 : 19 }}>
                {typedOver ? 'Estimate Overridden' : 'Estimate'}
              </div>
            )}
            {typedOver && <ShippingOverrideReason value={shippingOverrideReason} onChange={setShippingOverrideReason} locked={costFieldLocked} />}
            {shippingCostBlank && !chargeLocked && (
              <div style={{ fontSize: 11, color: '#b45309', textAlign: 'right', marginTop: -2, marginBottom: 4, paddingRight: costFieldLocked ? 0 : 19 }}>
                To be added
              </div>
            )}
            {chargeLocked && (
              <div style={{ fontSize: 11, color: '#5a6b85', textAlign: 'right', marginTop: -2, marginBottom: 4, paddingRight: costFieldLocked ? 0 : 19 }}>
                Invoiced
              </div>
            )}
            {selectedOptions.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0' }}>
                <span>Options Cost{basisSuffix}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {formatPrice(viewInc ? totals.options : Math.round(optionsCostEx * 100) / 100)}
                  {!costFieldLocked && <span aria-hidden="true" style={{ width: 15, visibility: 'hidden' }} />}
                </span>
              </div>
            )}
            {gstApplicable && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0' }}>
                <span>{viewInc ? 'Includes GST' : 'GST'}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {formatPrice(totals.gst)}
                  {!costFieldLocked && <span aria-hidden="true" style={{ width: 15, visibility: 'hidden' }} />}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 16, fontWeight: 700, padding: '8px 0', borderTop: '1px solid #b3bbc7', marginTop: 4 }}>
              <span>Total{gstApplicable ? ' (inc GST)' : ''}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {formatPrice(totals.total)}
                {!costFieldLocked && <span aria-hidden="true" style={{ width: 15, visibility: 'hidden' }} />}
              </span>
            </div>
            {lines.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#5a6b85', padding: '4px 0' }}>
                <span>Total Weight</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: costFieldLocked ? 0 : 19 }}>
                  {anyMissingWeight && <NeedsWeightPill />}
                  {formatWeight(totalWeight)}
                </span>
              </div>
            )}
            {shipmentsWithWeight.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#5a6b85', padding: '4px 0' }}>
                <span>Total Shipment Weight</span>
                <span style={{ paddingRight: costFieldLocked ? 0 : 19 }}>{formatShipmentWeight(totalShipmentWeight)}</span>
              </div>
            )}
          </div>
        </div>
      </Accordion>
      </div>

      {id && (
        <DocumentPayments
          basePath="sales-orders"
          documentId={id}
          recordOpen={recordPaymentOpen}
          onRecordOpenChange={setRecordPaymentOpen}
          onSummary={setPaymentSummary}
          onChanged={() => setHistoryVersion((v) => v + 1)}
          canUpdate={canUpdate}
          reloadKey={orderMeta}
        />
      )}

      <div style={{ marginTop: 16 }}>
        {canSave && (
          <>
            <SaveButton type="button" onClick={() => handleSave(false)} loading={saveMode === 'stay'} disabled={!customerId || saving} />
            <SaveButton type="button" onClick={() => handleSave(true)} loading={saveMode === 'close'} disabled={!customerId || saving} style={{ marginLeft: 8 }}>
              {saveMode === 'close' ? 'Saving...' : 'Save & Close'}
            </SaveButton>
            <CancelButton onClick={handleCancel} disabled={saving} style={{ marginLeft: 8 }} />
          </>
        )}
        {!canSave && <CancelButton onClick={handleCancel} disabled={saving}>Close</CancelButton>}
      </div>

      {shipDialogOpen && (
        <ShipConfirmDialog
          open
          onOpenChange={(next) => { if (!next) setShipDialogOpen(false); }}
          lines={shipOrder.lines}
          packages={shipOrder.packages}
          order={orderMeta?.savedCarriage}
          orderDate={orderMeta?.orderDate}
          shipping={shipping}
          onConfirm={handleConfirmShip}
        />
      )}

      {reestimateDialog}

      {cancelDialogLine && (
        <ConfirmDialog
          open
          onOpenChange={(next) => { if (!next) setCancelDialogLine(null); }}
          title="Cancel Backorder"
          description={`${cancelDialogLine.product?.sku} — ${formatNumber(outstandingQty(cancelDialogLine), { maxDecimals: 4 })} outstanding. This can't be undone.`}
          footer={(
            <>
              <CancelButton onClick={() => setCancelDialogLine(null)} disabled={cancelling} />
              <button
                type="button"
                onClick={handleConfirmCancelBackorder}
                disabled={cancelling || !canConfirmCancel}
                style={{ padding: '8px 14px', border: 'none', color: '#fff', background: canConfirmCancel ? '#b91c1c' : '#c7ccd4', borderRadius: 6, cursor: canConfirmCancel ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}
              >
                {cancelling ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 13 }}>
              Qty to cancel
              <NumberInput
                min={1}
                max={outstandingQty(cancelDialogLine)}
                maxDecimals={4}
                value={cancelQty}
                onChange={(e) => setCancelQty(e.target.value)}
                style={{ display: 'block', width: 120, marginTop: 4, fontSize: 13 }}
              />
            </label>
            <label style={{ fontSize: 13 }}>
              Reason <RequiredMark />
              <textarea
                className="styled-input"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                style={{ display: 'block', width: '100%', marginTop: 4, fontSize: 13, resize: 'vertical' }}
              />
            </label>
          </div>
        </ConfirmDialog>
      )}

      {showReport && (
        <ReportOverlay onClose={() => setShowReport(null)}>
          {showReport === 'sales-order'
            ? <SalesOrderReportView salesOrderId={id} onClose={() => setShowReport(null)} />
            : <PickSlipReportView orderType="sales_order" orderId={id} onClose={() => setShowReport(null)} />}
        </ReportOverlay>
      )}
    </div>
    <div className="detail-timeline-sidebar">
      {id && <SalesOrderTimeline key={`timeline-${historyVersion}`} documentId={id} sourceQuote={orderMeta?.sourceQuote} shipmentsLink={shipmentsLink} />}
    </div>
    </div>
  );
}

export default SalesOrderPage;
